import { useState, useEffect, useCallback } from 'react';

function checkIsWebViewSync(): { isWebView: boolean; reason: string } {
  const urlParams = new URLSearchParams(window.location.search);
  const isWebViewParam = urlParams.get('webview');
  if (isWebViewParam === 'true' || isWebViewParam === '1') {
    return { isWebView: true, reason: 'URLParam' };
  }

  if ((window as any).ReactNativeWebView) {
    return { isWebView: true, reason: 'ReactNativeWebView' };
  }

  if ((window as any).webkit?.messageHandlers) {
    return { isWebView: true, reason: 'WKWebView' };
  }

  if ((window as any).Android) {
    return { isWebView: true, reason: 'Android' };
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const isIosDevice = /iphone|ipod|ipad/.test(userAgent);

  const isWebViewUserAgent =
    userAgent.includes('wv') ||
    userAgent.includes('webview') ||
    (isIosDevice && !userAgent.includes('safari'));

  if (isWebViewUserAgent) {
    return { isWebView: true, reason: 'UserAgent' };
  }

  return { isWebView: false, reason: 'None' };
}

export function useIsWebView(): boolean {
  const initialCheck = checkIsWebViewSync();
  const [isWebView, setIsWebView] = useState(initialCheck.isWebView);

  const checkWebView = useCallback(() => {
    const result = checkIsWebViewSync();
    if (result.isWebView && !isWebView) {
      setIsWebView(true);
    }
    return result.isWebView;
  }, [isWebView]);

  useEffect(() => {
    if (checkWebView()) return;

    const timeouts = [100, 300, 500, 1000, 2000, 3000];
    const timeoutIds: NodeJS.Timeout[] = [];

    timeouts.forEach((delay) => {
      const id = setTimeout(() => {
        if (checkWebView()) {
          timeoutIds.forEach((tid) => clearTimeout(tid));
        }
      }, delay);
      timeoutIds.push(id);
    });

    return () => {
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, [checkWebView]);

  return isWebView;
}

/**
 * Envia mensagem para fechar a WebView.
 * Tenta ReactNativeWebView.postMessage, iOS WKWebView, Android e window.postMessage como fallback.
 */
export function closeWebView(): void {
  const message = JSON.stringify({ type: 'close' });

  try {
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(message);
      return;
    }

    if ((window as any).webkit?.messageHandlers?.close) {
      (window as any).webkit.messageHandlers.close.postMessage(message);
      return;
    }

    if ((window as any).webkit?.messageHandlers?.callbackHandler) {
      (window as any).webkit.messageHandlers.callbackHandler.postMessage(message);
      return;
    }

    if ((window as any).Android?.finish && typeof (window as any).Android.finish === 'function') {
      (window as any).Android.finish();
      return;
    }

    window.postMessage(message, window.location.origin);
  } catch (error) {
    console.warn('Erro ao tentar fechar webview:', error);
  }
}

export default useIsWebView;
